import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Box, Rating } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate,useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { makeStyles } from "@mui/styles";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions"
import GoBackButton from "components/GoBackButton";
import { getLangKey } from "common/src/other/getLangKey";
import { getUserById } from "common/src/actions/usersactions";
import { getDriverActiveCar } from "common/src/actions/caractions";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "10px",
    backgroundColor: colors.WHITE,
    minHeight: 100,
    marginTop: 5,
    marginBottom: 20,
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
  },
  txt: {
    padding: 10,
    fontWeight: "bold",
    minHeight: 60,
    backgroundColor: SECONDORY_COLOR,
    color: colors.BLACK,
    boxShadow: 3,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [driverProfile, setDriverProfile] = useState(null);
  const [driverCar, setDriverCar] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const role = useSelector(state => state.auth.profile.usertype);
  const { state } = useLocation();
  const cartypes = useSelector(state => state.cartypes.cars);


  function formatAmount(value, decimal, country) {
    const number = parseFloat(value || 0);
    if (country === "Vietnam") {
      return number.toLocaleString("vi-VN", {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal
      });
    } else {
      return number.toLocaleString("en-US", {
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal
      });
    }
  }

  const pickFirst = (obj, keys) => {
    if (!obj) return "";
    for (const k of keys) {
      const v = k.includes(".")
        ? k.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj)
        : obj[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  const formatDob = (v) => {
    if (!v) return "";
    if (typeof v === "number") return moment(v).format("ll");
    if (typeof v === "string") {
      const s = v.trim();
      if (/^\d+$/.test(s)) return moment(parseInt(s, 10)).format("ll");
      const m = moment(s);
      return m.isValid() ? m.format("ll") : s;
    }
    if (typeof v === "object" && v.seconds) return moment(v.seconds * 1000).format("ll");
    return String(v);
  };


  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        setData(booking);
      } else {
        navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  useEffect(() => {
    let active = true;
    (async () => {
      const uid = data?.driver;
      if (!uid) {
        if (active) setDriverProfile(null);
        return;
      }
      const profile = await getUserById(uid);
      if (active) setDriverProfile(profile);
    })();
    return () => {
      active = false;
    };
  }, [data?.driver]);

  useEffect(() => {
    let active = true;
    (async () => {
      const uid = data?.customer;
      if (!uid) {
        if (active) setCustomerProfile(null);
        return;
      }
      const profile = await getUserById(uid);
      if (active) setCustomerProfile(profile);
    })();
    return () => {
      active = false;
    };
  }, [data?.customer]);

  // Placa e cor vêm do cadastro do carro (fonte da verdade), não do snapshot do booking.
  useEffect(() => {
    let active = true;
    (async () => {
      const uid = data?.driver;
      if (!uid) {
        if (active) setDriverCar(null);
        return;
      }
      const car = await getDriverActiveCar(uid, data?.car_image);
      if (active) setDriverCar(car);
    })();
    return () => {
      active = false;
    };
  }, [data?.driver, data?.car_image]);


  const carTypeObj = cartypes && data?.carType ? cartypes.find(ct => ct.name === data.carType) : null;

  const driverName = data?.driver_name
    ? data.driver_name
    : [driverProfile?.firstName, driverProfile?.lastName].filter(Boolean).join(" ") || driverProfile?.name || "";

  const driverContact = data?.driver_contact || pickFirst(driverProfile, ["mobile", "phone", "contact", "mobileNumber", "phoneNumber", "profile.mobile", "profile.phone", "profile.contact"]);
  const driverEmail = data?.driver_email || pickFirst(driverProfile, ["email", "emailAddress", "profile.email"]);
  const driverImage = data?.driver_image || pickFirst(driverProfile, ["profile_image", "profileImage", "image", "photoURL", "profileImageUrl", "profile.photoURL", "profile.profileImage", "profile.profile_image"]);

  const driverDob = formatDob(pickFirst(driverProfile, ["birthDate", "dob", "dateOfBirth", "birthday", "profile.birthDate", "profile.dob", "profile.dateOfBirth", "profile.birthday"]));
  const driverGender = pickFirst(driverProfile, ["gender", "sexo", "profile.gender", "profile.sexo"]);
  const driverRg = pickFirst(driverProfile, ["rg", "RG", "profile.rg", "documents.rg", "docs.rg"]);
  const driverCpf = pickFirst(driverProfile, ["cpf", "CPF", "taxId", "nationalId", "profile.cpf", "documents.cpf", "docs.cpf"]);

  // Placa e cor lidas do carro; cai para o booking só se o carro não trouxer.
  const vehiclePlaca = driverCar?.placa || data?.placa || "";
  const vehicleColorVal = driverCar?.vehicleColor || driverCar?.vehicle_color || data?.vehicleColor || "";

  const customerName = data?.customer_name
    ? data.customer_name
    : [customerProfile?.firstName, customerProfile?.lastName].filter(Boolean).join(" ") || customerProfile?.name || "";

  const customerContact = data?.customer_contact || pickFirst(customerProfile, ["mobile", "phone", "contact", "mobileNumber", "phoneNumber", "profile.mobile", "profile.phone", "profile.contact"]);
  const customerEmail = data?.customer_email || pickFirst(customerProfile, ["email", "emailAddress", "profile.email"]);
  const customerImage = data?.customer_image || pickFirst(customerProfile, ["profile_image", "profileImage", "image", "photoURL", "profileImageUrl", "profile.photoURL", "profile.profileImage", "profile.profile_image"]);

  const customerDob = formatDob(pickFirst(customerProfile, ["birthDate", "dob", "dateOfBirth", "birthday", "profile.birthDate", "profile.dob", "profile.dateOfBirth", "profile.birthday"]));
  const customerGender = pickFirst(customerProfile, ["gender", "sexo", "profile.gender", "profile.sexo"]);
  const customerRg = pickFirst(customerProfile, ["rg", "RG", "profile.rg", "documents.rg", "docs.rg"]);
  const customerCpf = pickFirst(customerProfile, ["cpf", "CPF", "taxId", "nationalId", "profile.cpf", "documents.cpf", "docs.cpf"]);


  const renderGridItem = (item, isRTL) => {
    return (
      <Grid
        key={item.key}
        container
        spacing={1}
        sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
        style={{
          justifyContent: "center",
          alignItems: "center",
          minHeight: 60,
          ...item.addressGridStyle
        }}
      >
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 16,
              padding: 2,
              textAlign: isRTL === "rtl" ? "right" : "left",
              fontFamily:FONT_FAMILY,
              ...item.typographyStyleAddress
            }}
          >
            {item.label}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 18,
              padding: 2,
              letterSpacing: -1,
              textAlign: "center",
              fontFamily:FONT_FAMILY,
            }}
          >
            -----
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography
            style={{
              fontSize: 16,
              padding: 2,
              textAlign: isRTL === "rtl" ? "left" : "right",
              wordBreak: 'break-word',
              fontFamily:FONT_FAMILY,
              ...item.style,
            }}
          >
            {item.value}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderTypography = (text) => {
    return (
      <Typography
      className={classes.txt}
      style={{
        borderBottomRightRadius: isRTL ? "90px" : "",
        borderBottomLeftRadius: isRTL ? "" : "90px",
        fontFamily:FONT_FAMILY,
      }}
      variant="h5"
      >
        {text}
      </Typography>
    );
  };

  // Firebase RTDB pode devolver arrays como objetos indexados; normaliza para array.
  const toArray = (v) =>
    Array.isArray(v) ? v : (v && typeof v === "object" ? Object.values(v) : []);

  const renderMerchandiseCard = () => {
    const merc = data?.mercadoriaDetails;
    const volumes = toArray(merc?.volumes);
    if (!merc || volumes.length === 0) return null;

    return (
      <Grid item xs={12}>
        <Card className={classes.card}>
          {renderTypography(t("goods_details") || "Detalhes da Mercadoria")}
          <Grid container direction="column" style={{ padding: 15 }}>
            {[
              { key: "goods_type", label: t("goods_type") || "Tipo", value: data?.mercadoriaSelected },
              { key: "total_volumes", label: t("total_volumes"), value: merc?.totalVolumes || volumes.length },
              { key: "total_weight", label: t("total_weight"), value: merc?.totalPeso ? `${merc.totalPeso} kg` : "" },
              { key: "total_value", label: t("total_value"), value: merc?.totalValor ? `${settings.symbol} ${merc.totalValor}` : "" },
            ].map((item) => (item.value ? renderGridItem(item, isRTL) : null))}

            {volumes.map((v, vi) => {
              const anexos = toArray(v?.anexos);
              const dims = [v?.altura, v?.largura, v?.profundidade].filter(Boolean).join(" x ");
              return (
                <Box key={vi} style={{ borderTop: "1px solid #eee", marginTop: 10, paddingTop: 10 }}>
                  <Typography style={{ fontWeight: "bold", fontFamily: FONT_FAMILY, marginBottom: 6 }}>
                    {t("volume")} {vi + 1}
                  </Typography>
                  {[
                    { key: `prod-${vi}`, label: t("product") || "Produto", value: v?.produto },
                    { key: `qty-${vi}`, label: t("qty") || "Qtd", value: v?.quantidade },
                    { key: `nf-${vi}`, label: t("invoice") || "NF", value: v?.notaFiscal },
                    { key: `peso-${vi}`, label: t("weight") || "Peso", value: v?.peso },
                    { key: `valor-${vi}`, label: t("value") || "Valor", value: v?.valor },
                    { key: `dim-${vi}`, label: t("dimensions") || "Dimensões", value: dims },
                  ].map((item) => (item.value ? renderGridItem(item, isRTL) : null))}

                  {anexos.length > 0 && (
                    <Box style={{ marginTop: 8 }}>
                      <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 16, marginBottom: 4 }}>
                        {t("attachments") || "Anexos"}:
                      </Typography>
                      {anexos.map((a, ai) => (
                        <Box key={ai} style={{ marginBottom: 4 }}>
                          <a
                            href={a?.url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "#085c91", wordBreak: "break-all", fontFamily: FONT_FAMILY }}
                          >
                            📎 {a?.name || `${t("attachment") || "Anexo"} ${ai + 1}`}
                          </a>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Grid>
        </Card>
      </Grid>
    );
  };

  return (
    <>
         <GoBackButton isRTL={isRTL}  onClick={() => { navigate('/bookings', { state: { pageNo: state?.pageNo }} ) }} />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}> 
              {renderTypography(t("ride_information"))}
                <Grid container direction="column" style={{ padding: 10 }}>
                  {data &&
                    [
                      { key: "booking_id", label: t("booking_id"), value: data.id },
                      { key: "booking_ref", label: t("booking_ref"), value: data.reference },
                      {
                        key: "booking_status_web",
                        label: t("booking_status_web"),
                        value: t(data.status),
                        style: {
                          backgroundColor:
                          data?.status === "CANCELLED"
                                ? colors.RED
                                : data?.status === "COMPLETE"
                                  ? colors.GREEN
                                  : colors.YELLOW,
                          color:colors.WHITE,
                          fontWeight: "bold",
                          borderRadius: "10px",
                          textAlign: "center",
                          padding: 3,
                        },
                      },
                      {
                        key: "trip_start_date", label: t("trip_start_date"), value: data?.tripdate
                          ? moment(data?.tripdate).format("lll")
                          : ""
                      },
                      { key: "trip_start_time", label: t("trip_start_time"), value: data?.trip_start_time },
                      { key: "trip_end_time", label: t("trip_end_time"), value: data?.trip_end_time },
                      {
                        key: "parcel_type_web", label: t("parcel_type_web"), value: data.parcelTypeSelected
                          ? settings.swipe_symbol
                            ? `${t(getLangKey(data.parcelTypeSelected.description))}  ${data.parcelTypeSelected.amount}${settings.symbol}`
                            : `${t(getLangKey(data.parcelTypeSelected.description))}  ${settings.symbol}${data.parcelTypeSelected.amount}`
                          : ""
                      },
                      {
                        key: "parcel_option", label: t("parcel_option"), value: data.optionSelected
                          ? settings.swipe_symbol
                            ? `${t(getLangKey(data.optionSelected.description))}  ${data.optionSelected.amount}${settings.symbol}`
                            : `${t(getLangKey(data.optionSelected.description))}  ${settings.symbol}${data.optionSelected.amount}`
                          : ""
                      },
                      { key: "deliveryPerson", label: t("deliveryPerson"), value: data?.deliveryPerson },
                      {
                        key: "deliveryPersonPhone", label: t("deliveryPersonPhone"), value: data.deliveryPersonPhone ? (settings.AllowCriticalEditsAdmin
                          ? data.deliveryPersonPhone
                          : t("hidden_demo")) : ""
                      },
                      { key: "take_pickup_image_web", label: t("take_pickup_image_web"), value: data.pickup_image ? data.pickup_image : "", type: 'image', alt: "pickup_image" },
                      { key: "take_deliver_image_web", label: t("take_deliver_image_web"), value: data.deliver_image ? data.deliver_image : "", type: 'image', alt: "deliver_image" },
                      { key: "tripInstructions", label: t("tripInstructions"), value: data?.tripInstructions },
                      { key: "cancellation_reason", label: t("cancellation_reason"), value: t(getLangKey(data?.reason)) },
                      { key: "otp", label: t("otp"), value: data?.otp  ? t("true") : t("false")},
                      { key: "total_time", label: t("total_time"), value: data?.total_trip_time },
                      {
                        key: "distance_web", label: t("distance_web"), value: data.distance ? (settings.convert_to_mile
                          ? data?.distance + t("mile")
                          : data?.distance + " " + t("km")) : ""
                      },
                      { key: "pickUpInstructions_web", label: t("pickUpInstructions_web"), value: data?.pickUpInstructions },
                      { key: "deliveryInstructions", label: t("deliveryInstructions"), value: data?.deliveryInstructions },
                      { key: "otherPerson", label: t("otherPerson"), value: data?.otherPerson },
                      { key: "otherPersonPhone", label: t("otherPersonPhone"), value: data?.otherPersonPhone },
                      { key: "feedback", label: t("feedback"), value: data?.feedback },
                      { key: "you_rated_text", label: t("you_rated_text"), value: <Rating  name="half-rating-read" value={data?.rating || 0}   precision={0.5} readOnly /> },
                      {
                        key: "pickup_address", label: t("pickup_address"), value: data?.pickupAddress, addressGridStyle: {
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "row",
                          minHeight: 60,
                          marginBottom: 20,
                        }, typographyStyleAddress: { color:colors.GREEN }
                      },
                      { key: "drop_address", label: t("drop_address"), value: data?.dropAddress, typographyStyleAddress: { color:colors.RED, } },
                    ].map((item) =>
                      item.type === "image" && item.value ? (
                        <Grid
                          key={item.key}
                          container
                          spacing={1}
                          sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                          style={{
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 60,
                          }}
                        >
                          <Grid item xs={4}>
                            <Typography
                              style={{
                                fontSize: 16,
                                padding: 2,
                                textAlign: isRTL === "rtl" ? "right" : "left",
                                fontFamily:FONT_FAMILY,
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              style={{
                                fontSize: 18,
                                padding: 2,
                                letterSpacing: -1,
                                textAlign: "center",
                                fontFamily:FONT_FAMILY,
                              }}
                            >
                              -----
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <img
                              src={item.value}
                              alt={item.alt}
                              style={{
                                width: 120,
                                height: 90,
                              }}
                            />
                          </Grid>
                        </Grid>
                      ) :
                        item.value ?renderGridItem(item, isRTL) : null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("payment_info"))}
                <Grid container direction="column" style={{ paddingRight: 15, paddingLeft: 15, paddingBottom: 15 }}>
                  {carTypeObj && [
                    { key: "base_fare", label: t("base_fare"), value: settings.swipe_symbol ? formatAmount(carTypeObj.base_fare, settings.decimal, settings.country) + " " + settings.symbol : settings.symbol + " " + formatAmount(carTypeObj.base_fare, settings.decimal, settings.country) },
                    { key: "rate_per_unit_distance", label: t("rate_per_unit_distance"), value: settings.swipe_symbol ? formatAmount(carTypeObj.rate_per_unit_distance, settings.decimal, settings.country) + " " + settings.symbol + "/" + (settings.convert_to_mile ? t('mile') : t('km')) : settings.symbol + " " + formatAmount(carTypeObj.rate_per_unit_distance, settings.decimal, settings.country) + "/" + (settings.convert_to_mile ? t('mile') : t('km')) },
                    { key: "rate_per_hour", label: t("rate_per_hour"), value: settings.swipe_symbol ? formatAmount(carTypeObj.rate_per_hour, settings.decimal, settings.country) + " " + settings.symbol : settings.symbol + " " + formatAmount(carTypeObj.rate_per_hour, settings.decimal, settings.country) },
                    { key: "min_fare", label: t("min_fare"), value: settings.swipe_symbol ? formatAmount(carTypeObj.min_fare, settings.decimal, settings.country) + " " + settings.symbol : settings.symbol + " " + formatAmount(carTypeObj.min_fare, settings.decimal, settings.country) },
                    { key: "fleet_admin_fee", label: t("fleet_admin_fee"), value: settings.swipe_symbol ? formatAmount(carTypeObj.fleet_admin_fee, settings.decimal, settings.country) + " " + settings.symbol : settings.symbol + " " + formatAmount(carTypeObj.fleet_admin_fee, settings.decimal, settings.country) },
                    { key: "convenience_fees", label: t("convenience_fee"), value: carTypeObj
                        ? carTypeObj.convenience_fee_type === "flat"
                          ? (settings.swipe_symbol
                              ? formatAmount(carTypeObj.convenience_fees, settings.decimal, settings.country) + " " + settings.symbol
                              : settings.symbol + " " + formatAmount(carTypeObj.convenience_fees, settings.decimal, settings.country))
                          : carTypeObj.convenience_fee_type === "percentage"
                            ? carTypeObj.convenience_fees + " %"
                            : ""
                        : ""
                      },
                       ].map((item) => renderGridItem(item, isRTL))}
                  <Box sx={{ borderBottom: '1px solid #ccc', marginY: 2 }} />
                  {data &&
                    [
                      {key: "trip_cost", label: t("trip_cost"), value:( role === 'customer' && (['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1)) ?
                         t('bid').toUpperCase() :
                        settings.swipe_symbol? ( formatAmount(data?.trip_cost, settings.decimal, settings.country) + " " + settings.symbol): (settings.symbol + " " + formatAmount(data?.trip_cost, settings.decimal, settings.country))
                      },
                      {
                        key: "Customer_paid", label: t("Customer_paid"), value: data?.customer_paid ? (settings.swipe_symbol
                            ? formatAmount(data?.customer_paid, settings.decimal, settings.country) + " " + settings.symbol
                            : settings.symbol + " " + formatAmount(data?.customer_paid, settings.decimal, settings.country)) : ""
                      },
                      {
                        key: "discount_ammount",
                        label: t("discount_ammount"),
                        value: settings.swipe_symbol
                          ? formatAmount(data?.discount, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " + formatAmount(data?.discount, settings.decimal, settings.country),
                      },
                      {
                        key: "driver_share", label: t("driver_share"), value: ( role === 'customer' && (['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1)) ?
                        '--' :
                        settings.swipe_symbol ? formatAmount(data?.driver_share, settings.decimal, settings.country) + " " + settings.symbol : settings.symbol + " " + formatAmount(data?.driver_share, settings.decimal, settings.country)
                      },
                      {
                        key: "fleet_admin_comission", label: t("fleet_admin_comission"), value: ( role === 'customer' && (['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1)) ?
                        '--' :
                        data?.fleetCommission && parseFloat(data?.fleetCommission) > 0 ? (settings.swipe_symbol
                          ? formatAmount(data?.fleetCommission, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " + formatAmount(data?.fleetCommission, settings.decimal, settings.country)) : ""
                      },
                      {
                        key: "convenience_fee", label: t("convenience_fee"), value: ( role === 'customer' && (['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1)) ?
                        '--' :
                        settings.swipe_symbol
                          ? formatAmount(data?.convenience_fees, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " + formatAmount(data?.convenience_fees, settings.decimal, settings.country)
                      },
                      {
                        key: "tipamount", label: t("tipamount"), value:data?.tipamount && (settings.swipe_symbol
                          ? formatAmount(data?.tipamount, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " +  formatAmount(data?.tipamount, settings.decimal, settings.country))
                      },
                      {
                        key: "cancellationFee", label: t("cancellationFee"), value: settings.swipe_symbol
                          ? data.cancellationFee ? formatAmount(data.cancellationFee, settings.decimal, settings.country) : (0).toFixed(settings.decimal) + " " +
                            settings.symbol
                          : settings.symbol + " " + formatAmount(data.cancellationFee, settings.decimal, settings.country)
                            ? formatAmount(data.cancellationFee, settings.decimal, settings.country)
                            : (0).toFixed(settings.decimal)
                      },
                      {
                        key: "payment_gateway", label: t("payment_gateway"), value: data?.gateway
                      },
                      { key: "payment_mode_web", label: t("payment_mode_web"), value: data?.payment_mode ? data.payment_mode : "" },
                      {
                        key: "cash_payment_amount", label: t("cash_payment_amount"), value: data?.cashPaymentAmount ? (settings.swipe_symbol
                          ? formatAmount(data?.cashPaymentAmount, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " + formatAmount(data?.cashPaymentAmount, settings.decimal, settings.country)) : ""
                      },
                      {
                        key: "card_payment_amount", label: t("card_payment_amount"), value:
                          data?.cardPaymentAmount ? (settings.swipe_symbol
                            ? formatAmount(data?.cardPaymentAmount, settings.decimal, settings.country) + " " + settings.symbol
                            : settings.symbol + " " + formatAmount(data?.cardPaymentAmount, settings.decimal, settings.country)) : ""
                      },
                      {
                        key: "wallet_payment_amount", label: t("wallet_payment_amount"), value:
                          data?.usedWalletMoney ? (settings.swipe_symbol
                            ? formatAmount(data?.usedWalletMoney, settings.decimal, settings.country) + " " + settings.symbol
                            : settings.symbol + " " + formatAmount(data?.usedWalletMoney, settings.decimal, settings.country)) : ""
                      },
                      {
                        key: "payable_ammount", label: t("payable_ammount"), value: data?.payableAmount ? (settings.swipe_symbol
                          ? formatAmount(data?.payableAmount, settings.decimal, settings.country) + " " + settings.symbol
                          : settings.symbol + " " + formatAmount(data?.payableAmount, settings.decimal, settings.country)) : ""
                      },
                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("driver_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {driverImage ? (
                      <Avatar
                        alt="driver profile image"
                        src={driverImage}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "driver_id", label: t("driver_id"), value: data?.driver ? data.driver : "" },
                      { key: "driver_name", label: t("driver_name"), value: driverName ? driverName : "" },
                      {
                        key: "driver_contact", label: t("driver_contact"), value: driverContact ? (settings.AllowCriticalEditsAdmin
                          ? driverContact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "driver_email", label: t("driver_email"), value: driverEmail ? (settings.AllowCriticalEditsAdmin
                          ? driverEmail
                          : t("hidden_demo")) : ""
                      },
                      { key: "driver_dob", label: t("birth_date"), value: driverDob ? (settings.AllowCriticalEditsAdmin ? driverDob : t("hidden_demo")) : "" },
                      { key: "driver_gender", label: t("gender"), value: driverGender ? (settings.AllowCriticalEditsAdmin ? driverGender : t("hidden_demo")) : "" },
                      { key: "driver_rg", label: t("rg_placeholder"), value: driverRg ? (settings.AllowCriticalEditsAdmin ? driverRg : t("hidden_demo")) : "" },
                      { key: "driver_cpf", label: t("cpf"), value: driverCpf ? (settings.AllowCriticalEditsAdmin ? driverCpf : t("hidden_demo")) : "" },
                      { key: "car_type", label: t("car_type"), value: data?.carType ? t(getLangKey(data.carType)) : "" },
                      { key: "vehicle_no", label: t("vehicle_no"), value: data?.vehicle_number ? data.vehicle_number : "" },
                      { key: "placa", label: t("placa"), value: vehiclePlaca },
                      { key: "renavam", label: t("renavam"), value: data?.renavam ? data.renavam : "" },
                      { key: "crlve", label: t("crlve"), value: data?.crlve ? data.crlve : "" },
                      { key: "vehicleColor", label: t("vehicle_color"), value: vehicleColorVal },
                      { key: "version", label: t("version"), value: data?.version ? data.version : "" },
                      { key: "capacity_kg", label: t("capacity_kg"), value: data?.capacity ? data.capacity : "" },
                      { key: "chassis", label: t("chassis"), value: data?.chassis ? data.chassis : "" },
                      { key: "state", label: t("state"), value: data?.state ? t(data.state) : "" },
                      { key: "fuel", label: t("fuel"), value: data?.fuel ? t(data.fuel) : "" },
                      { key: "bodywork", label: t("bodywork"), value: data?.bodywork ? t(data.bodywork) : "" },
                      { key: "vehicle_type", label: t("vehicle_type"), value: data?.vehicleType ? t(data.vehicleType) : "" },
                      { key: "wheel_type", label: t("wheel_type"), value: data?.wheelType ? t(data.wheelType) : "" },
                      { key: "fleetadmins", label: t("fleetadmins"), value: data?.fleetadmin && role === 'admin' ? data.fleetadmin : "" },
                      { key: "device_id", label: t("device_id"), value: data?.driverDeviceId ? data.driverDeviceId : "" },

                    ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <Grid item>
              <Card className={classes.card}>
                {renderTypography(t("customer_info"))}
                <Grid container direction="column" style={{ padding: 15 }}>
                  <Grid
                    item
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {customerImage ? (
                      <Avatar
                        alt="customer profile image"
                        src={customerImage}
                        style={{ width: 100, height: 100, objectFit: "cover" }}
                      />
                    ) : (
                      <AccountCircleIcon sx={{ width: 100, height: 100 }} />
                    )}
                  </Grid>
                  {data &&
                    [
                      { key: "customer_id", label: t("customer_id"), value: data?.customer ? data.customer : "" },
                      { key: "customer_name", label: t("customer_name"), value: customerName ? customerName : "" },
                      {
                        key: "customer_contact", label: t("customer_contact"), value: customerContact ? (settings.AllowCriticalEditsAdmin
                          ? customerContact
                          : t("hidden_demo")) : ""
                      },
                      {
                        key: "customer_email", label: t("customer_email"), value: customerEmail ? (settings.AllowCriticalEditsAdmin
                          ? customerEmail
                          : t("hidden_demo")) : ""
                      },
                      { key: "customer_dob", label: t("birth_date"), value: customerDob ? (settings.AllowCriticalEditsAdmin ? customerDob : t("hidden_demo")) : "" },
                      { key: "customer_gender", label: t("gender"), value: customerGender ? (settings.AllowCriticalEditsAdmin ? customerGender : t("hidden_demo")) : "" },
                      { key: "customer_rg", label: t("rg_placeholder"), value: customerRg ? (settings.AllowCriticalEditsAdmin ? customerRg : t("hidden_demo")) : "" },
                      { key: "customer_cpf", label: t("cpf"), value: customerCpf ? (settings.AllowCriticalEditsAdmin ? customerCpf : t("hidden_demo")) : "" },
                      ].map((item) =>
                      item.value ? renderGridItem(item, isRTL): null
                    )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
          {renderMerchandiseCard()}
        </Grid>
      </Box>
    </>
  );
}

export default BookingDetails;